import { ActivatedRoute } from '@angular/router';
import {
  switchMap,
  tap,
  map,
  take,
  toArray,
  mergeAll,
  filter,
  startWith,
  takeUntil
} from 'rxjs/operators';
import {
  Component,
  OnChanges,
  Input,
  SimpleChanges,
  ChangeDetectionStrategy,
  OnDestroy
} from '@angular/core';
import { Observable, iif, merge, combineLatest, race, Subject } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { PostsFacade } from 'src/app/modules/posts/store';

import {
  selectCommentsStatus,
  selectStatus,
  selectPostView,
  selectPosts,
  selectComments,
  selectCommentsCount,
  selectReplies
} from '../../../shared/ui/post/data/store/selector';
import { AuthService } from '../../../core/services/auth.service';
import { PostsService } from '../../../shared/ui/post/data/store/service';
import {
  IMessage,
  IMessageCtx
} from '../../../shared/components/message/message.component';
import { IPostsCtx } from '../../../shared/components/posts/list/posts.component';
import { IPost } from '../../../shared/models/post';
import { ICommentForm } from '../../../shared/models/comment';
import {
  loadPostsByUserId,
  loadPosts,
  addPost,
  deletePost,
  likePost,
  complainePost,
  sharePost,
  updatePost,
  paginatePostsByUser,
  paginatePosts,
  loadCommentsForPost,
  addCommentToPost,
  deleteComment,
  likeComment,
  replyComment,
  loadPostsById,
  pinPost,
  unpinPost,
  addPostToUser
} from '../../../shared/ui/post/data/store/actions';
import { selectProfileInfo } from '../info/store/info.reducer';
import { SavedPostsFacade } from '../../../shared/stores/posts/saved/posts.saved.facade';

@Component({
  selector: 'app-profile-posts',
  templateUrl: './posts.component.html',
  styleUrls: ['./posts.component.scss']
})
export class ProfilePostsComponent implements OnChanges, OnDestroy {
  readonly posts$ = this.store.pipe(select(selectPosts({ pinned: true })));
  @Input() userId: string;

  readonly status$ = this.store.pipe(select(selectStatus()));
  readonly isLoading$ = this.status$.pipe(
    map((status) => status === `LOADING`)
  );
  public isShowMessageFrom: Observable<boolean>;
  readonly messageCtx: IMessageCtx = {
    placeholder: `Post an update, article or question`,
    submit: (message) => this.submit(message),
    isSuggestArticle: true,
    isSupportTags: true,
    isSupportLinkPreview: true
  };

  private profileInfo$: Observable<any>;

  private _componentDestroy$ = new Subject();

  constructor(
    private store: Store,
    private router: ActivatedRoute,
    private actions: Actions,
    readonly auth: AuthService,
    private posts: PostsService,
    readonly savedPosts: SavedPostsFacade,
    private readonly postFacade: PostsFacade
  ) {
    this.profileInfo$ = store.pipe(
      select(selectProfileInfo),
      takeUntil(this._componentDestroy$)
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    // TODO: id should be a string
    // refacator to be explicit about that
    this.store.dispatch(loadPostsByUserId({ id: this.userId }));
  }

  ngOnDestroy() {
    this._componentDestroy$.next();
    this._componentDestroy$.complete();
  }

  get isAllowedToWritePost$() {
    return this.profileInfo$.pipe(
      filter((_) => !!_),
      map((_) => this.isAllowedToWritePost(_))
    );
  }

  public submit(message: IMessage) {
    if (this.userId) {
      return this.postFacade.addPostToUser(this.userId, message);
    }

    this.postFacade.addPost(message);
  }

  readonly postsCtx: IPostsCtx = {
    status: this.status$,
    delete: ({ id }) => this.store.dispatch(deletePost({ id })),
    like: ({ id }) => this.store.dispatch(likePost({ id })),
    complain: ({ id }) => this.store.dispatch(complainePost({ id })),
    share: ({ id }, message) => {
      this.postFacade.sharePost(id, message);
    },
    update: async (post, message) => {
      this.postFacade.updatePost(post.id, message);
    },
    pin: (post) => {
      if (post.pinned) {
        this.store.dispatch(unpinPost({ post }));
      } else {
        this.store.dispatch(pinPost({ post }));
      }
    },
    save: (post) => {
      this.savedPosts.toggleSave(post);
    },
    paginate: (_, page) => {
      // TODO: refactor to accept userId as number
      this.store.dispatch(
        paginatePostsByUser({ id: this.userId, nextPage: page })
      );
    },
    comments: {
      add: async (post, message) => {
        const form = await this.getCommentForm(post, message);
        this.store.dispatch(addCommentToPost({ comment: form }));
      },
      delete: (post, comment) => {
        this.store.dispatch(deleteComment({ comment }));
      },
      like: (post, comment) => {
        this.store.dispatch(likeComment({ comment }));
      },
      reply: async (post, comment, message) => {
        const form = await this.getCommentForm(post, message);
        form['comment_id'] = comment.id;
        this.store.dispatch(replyComment({ comment: form }));
      },
      get: (post) => {
        return {
          values: this.store.pipe(select(selectComments(post.id))),
          status: this.store.pipe(select(selectCommentsStatus(post.id))),
          count: this.store.pipe(select(selectCommentsCount(post.id)))
        };
      },
      paginate: (post, comments) => {
        this.store.dispatch(
          loadCommentsForPost({ post, offset: comments.length })
        );
      },
      replies: {
        get: (_, comment) => {
          return this.store.select(selectReplies(comment.id));
        }
      }
    }
  };

  protected async getCommentForm(
    post: IPost,
    message: IMessage
  ): Promise<ICommentForm> {
    return {
      post_id: post.id,
      text: message.text,
      foto: message.images,
      file: message.docs,
      links: message.linksToPreview
      // comment_id: comment.id,
    };
  }

  private isAllowedToWritePost(profile: any) {
    if (this.auth.isMyId(profile.id)) {
      return true;
    }

    return (
      profile.is_friend &&
      !profile.is_black &&
      !profile.in_black &&
      !(profile.type === 'org' && this.auth.userType() === 'student')
    );
  }
}
